'use client';

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { ArrowDown, Leaf, Zap, Sun, Droplets } from 'lucide-react';
import { HeroSectionMobile } from './HeroSectionMobile';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  stats: {
    value: number;
    label: string;
    icon: React.ReactNode;
  }[];
  onScrollDown: () => void;
  gemeindeName?: string;
  wappenLink?: string;
}

export function HeroSection(props: HeroSectionProps) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Don't render until we know if it's mobile or not (avoids hydration mismatch)
  if (isMobile === null) {
    return null;
  }

  // Use completely different component for mobile
  if (isMobile) {
    return <HeroSectionMobile {...props} />;
  }

  const {
    title,
    subtitle,
    stats,
    onScrollDown,
    gemeindeName,
    wappenLink,
  } = props;

  return (
    <section className="relative w-full h-screen flex flex-col justify-center overflow-hidden text-white z-10">
      {/* Animated Gradient Background */}
      <motion.div 
        className="absolute inset-0 z-0"
        animate={{
          background: [
            'linear-gradient(to bottom right, #064e3b, #065f46, #047857)',
            'linear-gradient(to bottom right, #047857, #059669, #10b981)',
            'linear-gradient(to bottom right, #065f46, #047857, #064e3b)',
            'linear-gradient(to bottom right, #064e3b, #065f46, #047857)',
          ],
        }}
        transition={{
          duration: 10,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }}
      />
      
      {/* Additional animated gradient layer for depth */}
      <motion.div 
        className="absolute inset-0 z-0 opacity-50"
        animate={{
          background: [
            'radial-gradient(circle at 20% 80%, #10b981 0%, transparent 50%)',
            'radial-gradient(circle at 80% 20%, #059669 0%, transparent 50%)',
            'radial-gradient(circle at 50% 50%, #047857 0%, transparent 50%)',
            'radial-gradient(circle at 20% 80%, #10b981 0%, transparent 50%)',
          ],
        }}
        transition={{
          duration: 15,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "loop",
        }}
      />
      
      {/* Grid overlay */}
      <div className="absolute inset-0 bg-grid-white/[0.05] [mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,0.8))]"></div>
      
      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <div className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Image 
                src="/logo-white.svg" 
                alt="Bürgerstrom Tirol Logo" 
                width={360} 
                height={80}
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6">
              {gemeindeName ? (
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <span>Energiezukunft in</span>
                  <div className="flex items-center gap-4">
                    {wappenLink && (
                      <div className="w-20 h-20 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/30 p-3 flex-shrink-0 shadow-lg">
                        <img 
                          src={wappenLink} 
                          alt={`Wappen ${gemeindeName}`}
                          className="w-full h-full object-contain drop-shadow-sm"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <span className="text-yellow-300 font-bold">{gemeindeName}</span>
                  </div>
                </div>
              ) : (
                title
              )}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-12">
              {subtitle}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 mb-4 mx-auto">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold mb-1">
                  {stat.value === 0 ? '-' : new Intl.NumberFormat('de-DE').format(stat.value)}
                  {stat.label.includes('€') && stat.value > 0 ? '€' : ''}
                </div>
                <div className="text-sm text-white/70">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col items-center justify-center"
          >
            <button
              onClick={onScrollDown}
              className="group flex flex-col items-center justify-center text-white/70 hover:text-white transition-colors"
              aria-label="Scroll down"
            >
              <span className="mb-2 text-sm font-medium">Mehr entdecken</span>
              <motion.div
                animate={{
                  y: [0, 10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-white/30 group-hover:border-white/60 transition-colors"
              >
                <ArrowDown className="w-4 h-4" />
              </motion.div>
            </button>
          </motion.div>
        </div>
      </div>
      
      {/* Floating energy icons */}
      <motion.div 
        className="absolute inset-0 pointer-events-none overflow-hidden"
        initial="hidden"
        animate="visible"
      >
        <motion.div 
          className="absolute top-1/4 left-1/4"
          variants={floatingIconVariants(1, 3)}
        >
          <Sun className="w-8 h-8 text-yellow-300/30" />
        </motion.div>
        <motion.div 
          className="absolute top-1/3 right-1/4"
          variants={floatingIconVariants(1.5, 4)}
        >
          <Droplets className="w-8 h-8 text-blue-300/30" />
        </motion.div>
        <motion.div 
          className="absolute bottom-1/4 right-1/3"
          variants={floatingIconVariants(2, 5)}
        >
          <Leaf className="w-8 h-8 text-green-300/30" />
        </motion.div>
        <motion.div 
          className="absolute bottom-1/3 left-1/4"
          variants={floatingIconVariants(2.5, 3.5)}
        >
          <Zap className="w-8 h-8 text-yellow-200/30" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// Helper function for floating animation variants
function floatingIconVariants(delay: number, duration: number) {
  return {
    hidden: { 
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: [0, 0.5, 0],
      y: [0, -40, 0],
      x: [0, Math.random() * 60 - 30, 0],
      transition: {
        delay,
        duration,
        repeat: Infinity,
        repeatType: 'reverse' as const,
        ease: 'easeInOut',
      },
    },
  };
}